import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Field } from '~/components/promotion/editor-section';
import { EntityMultiSelect } from '~/components/promotion/entity-multi-select';
import { toGreekCaps } from '~/lib/i18n/toGreekCaps';
import { MOCK_CATEGORIES } from '~/lib/lookups/mock';
import { cn } from '@/lib/utils';
import type { PromotionAction } from '~/lib/schemas/actions';
import { DiscountModeInput } from './discount-mode-input';

type Action = Extract<PromotionAction, { type: 'category-discount' }>;
type Row = Action['rows'][number];

interface Props {
	value: Action;
	onChange: (next: Action) => void;
}

const COLS = 'grid-cols-[1fr_70px_90px_72px_80px_32px]';
const HEAD = 'text-[10px] font-semibold tracking-[0.04em] text-foreground-subtle';

/** `''` → null so optional numeric inputs (max choices / max qty) can be cleared. */
const optionalInt = (raw: string): number | null => (raw === '' ? null : Number(raw) || null);

/**
 * CategoryDiscount (checkout-only): every cart product of a selected category is offered
 * the row's discount (% or € per unit). Mirrors the per-product offer UX; MaxChoices caps
 * how many offered items the customer may pick (empty = no limit).
 */
export function CategoryDiscountBody({ value, onChange }: Props) {
	const { t } = useTranslation();
	const setRow = (index: number, patch: Partial<Row>) =>
		onChange({ ...value, rows: value.rows.map((r, i) => (i === index ? { ...r, ...patch } : r)) });
	const removeRow = (index: number) => onChange({ ...value, rows: value.rows.filter((_, i) => i !== index) });
	const addRow = () =>
		onChange({ ...value, rows: [...value.rows, { categoryId: 0, minQty: 1, discount: 0, discountMode: 'percentage', maxQty: null }] });

	return (
		<div className="flex flex-col gap-4">
			<Field
				label={t('promotion.action.categoryDiscount.maxChoices')}
				help={t('promotion.action.categoryDiscount.maxChoicesHelp')}
				className="w-56">
				<Input
					type="number"
					min={1}
					value={value.maxChoices ?? ''}
					onChange={e => onChange({ ...value, maxChoices: optionalInt(e.target.value) })}
				/>
			</Field>

			<div className="flex flex-col gap-2">
				<div className={cn('grid items-center gap-2 px-1', COLS)}>
					<span className={HEAD}>{toGreekCaps(t('promotion.action.categoryDiscount.category'))}</span>
					<span className={HEAD}>{toGreekCaps(t('promotion.action.perProduct.minQty'))}</span>
					<span className={HEAD}>{toGreekCaps(t('promotion.action.perProduct.discount'))}</span>
					<span className={HEAD}>{toGreekCaps(t('promotion.action.discountMode.label'))}</span>
					<span className={HEAD}>{toGreekCaps(t('promotion.action.perProduct.maxQty'))}</span>
					<span />
				</div>

				{value.rows.map((row, index) => (
					<div key={index} className={cn('grid items-center gap-2', COLS)}>
						<EntityMultiSelect
							value={row.categoryId ? [row.categoryId] : []}
							onChange={ids => setRow(index, { categoryId: ids[ids.length - 1] ?? 0 })}
							options={MOCK_CATEGORIES}
							placeholder={t('common.search')}
						/>
						<Input type="number" min={1} value={row.minQty} onChange={e => setRow(index, { minQty: Number(e.target.value) || 0 })} />
						<Input
							type="number"
							min={0}
							max={row.discountMode === 'percentage' ? 100 : undefined}
							value={row.discount}
							onChange={e => setRow(index, { discount: Number(e.target.value) || 0 })}
						/>
						<DiscountModeInput value={row.discountMode} onChange={discountMode => setRow(index, { discountMode })} />
						<Input
							type="number"
							min={1}
							placeholder="∞"
							value={row.maxQty ?? ''}
							onChange={e => setRow(index, { maxQty: optionalInt(e.target.value) })}
						/>
						<button
							type="button"
							onClick={() => removeRow(index)}
							className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-muted hover:bg-muted hover:text-foreground"
							aria-label="remove row">
							<X size={14} />
						</button>
					</div>
				))}

				<button
					type="button"
					onClick={addRow}
					className="flex items-center justify-center gap-1.5 rounded-md border border-dashed border-border-strong py-2 text-sm text-foreground-muted transition-colors hover:border-brand hover:bg-brand-subtle hover:text-brand-subtle-fg">
					<Plus size={14} /> {t('promotion.action.categoryDiscount.add')}
				</button>
			</div>
		</div>
	);
}
